import {
  AfterViewInit,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { InputChangeEventDetail } from '@ionic/angular';
import {
  BehaviorSubject,
  filter,
  finalize,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { SmartBagCheckComponentComponent } from 'src/app/components/position-operations/active-carry-over-loads/smart-bag-check-component/smart-bag-check-component.component';
import { UldCountModalComponent } from 'src/app/components/position-operations/active-carry-over-loads/uld-count-modal/uld-count-modal.component';
import { AddEditUldModalComponent } from 'src/app/components/position-operations/outbound-scan-gle/add-edit-uld-modal/add-edit-uld-modal.component';
import { OutboundScanGleModalComponent } from 'src/app/components/position-operations/outbound-scan-gle/outbound-scan-gle-modal/outbound-scan-gle-modal.component';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpecialCharacters } from 'src/app/core/enums';
import { ModalFormValue } from 'src/app/core/models/common/data.model';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import {
  ActiveCarryOverLoads,
  ActiveCarryOverLoadsResponse,
} from 'src/app/core/models/position-operations/carry-over-loads.model';
import { GleLoadsResponse } from 'src/app/core/models/position-operations/outbound-scan-gle.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { ActiveCarryOverLoadsService } from 'src/app/core/services/position-operations/active-carry-over-loads/active-carry-over-loads.service';
import { CarryOverLoadsUtils } from 'src/app/core/utils/carry-over-loads/carry-over-loads.utils';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { TableFilterUtilsService } from 'src/app/core/utils/table-filter/table-filter-utils.service';
import { IMAGE_SRC, NO_RESULTS } from '../../shared-constants';
import {
  CONST,
  ColumnIds,
  ColumnText,
  SORT_LIST,
  SORT_OPTIONS,
  ULD_TYPE_OPTIONS,
} from './active-carry-over-loads-table.constant';

@Component({
  selector: 'app-active-carry-over-loads-table',
  templateUrl: './active-carry-over-loads-table.component.html',
  styleUrl: './active-carry-over-loads-table.component.scss',
  standalone: false,
})
export class ActiveCarryOverLoadsTableComponent
  implements OnInit, AfterViewInit
{
  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  private readonly _dialog = inject(MatDialog);
  readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);
  private locationService = inject(LocationService);

  readonly SRC = IMAGE_SRC;
  readonly CONST = { ...CONST };
  readonly SORT_LIST = SORT_LIST;
  readonly ULD_TYPE_OPTIONS = ULD_TYPE_OPTIONS;
  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly NO_RESULTS = NO_RESULTS;
  readonly SORT_OPTIONS = SORT_OPTIONS;
  readonly SpecialCharacters = SpecialCharacters;

  dataSource = new MatTableDataSource<ActiveCarryOverLoads>();

  // Create column search objects
  createAdminTableColumn(columnId: string) {
    return {
      itemId: columnId,
      ids: [],
      formControl: new FormControl(''),
    };
  }

  // Create admin table columns
  uldNumberData = this.createAdminTableColumn(ColumnIds.ULD_NUMBER);
  uldTypeData = this.createAdminTableColumn(ColumnIds.ULD_TYPE);
  sealNumberData = this.createAdminTableColumn(ColumnIds.SEAL_NUMBER);
  loadKeyData = this.createAdminTableColumn(ColumnIds.LOAD_KEY);
  loadChartData = this.createAdminTableColumn(ColumnIds.LOAD_CHART);
  movementData = this.createAdminTableColumn(ColumnIds.MOVEMENT);
  pkgCountData = this.createAdminTableColumn(ColumnIds.ULD_PKG_COUNT);
  uldCountData = this.createAdminTableColumn(ColumnIds.ULD_COUNT);
  loadCreateData = this.createAdminTableColumn(ColumnIds.LOAD_CREATE_DATE);
  originSortData = this.createAdminTableColumn(ColumnIds.ORIGIN_SORT);

  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  maxDate: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() + 3,
  );
  minDate: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() - 1,
  );
  closeDate = new Date();
  defaultCloseTime = this.getFormTime();

  carryLoadsForm!: FormGroup;
  dropdownOpen = [false, false];
  isLoadCreateDateSort = false;
  selectedUldNumberIndex: number | null = null;
  originalData: ActiveCarryOverLoads[] = [];
  selectedLoad!: ActiveCarryOverLoads;
  showEquipmentDetails: boolean = false;
  updateGleRequest!: GleLoadsResponse;
  sortCodeOption: Items[] = [];
  locationAutoLink: boolean = false;

  constructor(
    public commonUtils: CommonUtils,
    public tableFilterUtilsService: TableFilterUtilsService,
    private readonly fb: FormBuilder,
    private readonly activeCarryOverLoadsService: ActiveCarryOverLoadsService,
    private readonly carryOverLoadUtils: CarryOverLoadsUtils,
    private readonly dialog: MatDialog,
  ) {}

  /**
   * oninit lifecycle hook
   *
   * @memberof InterceptSummaryComponent
   */
  ngOnInit(): void {
    this.initForm();
    this.dataSource.filterPredicate =
      this.tableFilterUtilsService.createCustomFilter(
        'loadCreateDate' as keyof ActiveCarryOverLoads,
        '',
        ['origSort' as keyof ActiveCarryOverLoads],
      );

    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map(() => {
          this.resetState();
          this.getActiveLoads();
        }),
      )
      .subscribe();

    this.locationService
      .getCurrentLocation$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((value) => !!value),
        map((locationConfig) => {
          this.locationAutoLink =
            locationConfig?.location?.locationDetails?.autoLink ?? false;
        }),
      )
      .subscribe();
  }

  /**
   *
   * Setting up sorting for all columns
   * @memberof ArriveComponent
   */
  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.sort.disableClear = true;
  }

  timeChanged(event: string) {
    this.carryLoadsForm.get('closeTime')?.setValue(event);
    this.carryLoadsForm.updateValueAndValidity();
  }

  resetState() {
    this.selectedUldNumberIndex = null;
    if (this.carryLoadsForm) {
      this.carryLoadsForm.reset({
        closeDate: new Date(),
      });
    }
    if (
      this.uldNumberData?.formControl.value ||
      this.sealNumberData?.formControl.value
    ) {
      this.uldNumberData.formControl.setValue('');
      this.sealNumberData.formControl.setValue('');
    }
  }

  getActiveLoads() {
    this.activeCarryOverLoadsService
      .getActiveLoads()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((result) => {
          this.activeLoadsDataSet(result);
        }),
      )
      .subscribe();
  }

  activeLoadsDataSet(result: ActiveCarryOverLoadsResponse) {
    if (result.response.carryOverLoads.length == 0) {
      this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
    }
    this.dataSource.data = this.mapToDatasource(result.response.carryOverLoads);
    this.originalData = this.mapToDatasource(result.response.carryOverLoads);
    this.setSortCodeOption();
  }

  mapToDatasource(data: any[]): ActiveCarryOverLoads[] {
    const tableData: ActiveCarryOverLoads[] = [];
    data?.forEach((dat) => {
      const origin = dat?.loadKeyresponse?.origin;
      const isQualifierIncluded = dat?.sort?.length == 2;
      const origSort =
        dat?.loadKeyresponse?.discriminator == 'INTERNATIONAL'
          ? isQualifierIncluded
            ? `${dat.origSort}`
            : `${dat.origSort}${origin.qualifier || ''}`
          : dat.origSort;

      const record = {
        ...dat,
        origSort,
      };

      tableData.push(record);
    });
    return tableData;
  }

  setSortCodeOption() {
    this.sortCodeOption = [];
    this.dataSource.data.forEach((item) => {
      const isReasonTypeAdded = this.sortCodeOption.find(
        (i) => i.id === item.origSort,
      );
      if (!isReasonTypeAdded) {
        this.sortCodeOption.push({
          id: item.origSort,
          selected: true,
        });
      }
    });
  }

  /**
   * define form with form controls
   *
   * @memberof InterceptSummaryComponent
   */
  initForm() {
    this.carryLoadsForm = this.fb.group({
      closeDate: [new Date(), Validators.required],
      closeTime: [this.defaultCloseTime, Validators.required],
      percentFull: [
        '',
        Validators.compose([
          Validators.required,
          Validators.min(0),
          Validators.max(100),
        ]),
      ],
    });
  }

  /**
   * get form time on current date
   *
   * @memberof InterceptSummaryComponent
   */
  getFormTime() {
    const currentDate = new Date();
    const hours = currentDate.getHours().toString();
    const minutes = currentDate.getMinutes().toString();
    const tranformHours = hours.length > 1 ? hours : '0' + hours;
    const tranformMinutes = minutes.length > 1 ? minutes : '0' + minutes;

    return `${tranformHours}:${tranformMinutes}`;
  }

  /**
   * Triggers when a dropdown option is checked/unchecked
   *
   * @param {*} $event
   * @param {TableColumn} field
   * @memberof ActiveCarryOverLoadsTableComponent
   */
  onStatusChangeForDropdowns(
    $event: string | string[] | CustomEvent<InputChangeEventDetail>,
    field: TableColumn,
  ) {
    field.ids[0] = $event as unknown as Items;
    switch (field.itemId) {
      case ColumnIds.ULD_TYPE:
        this.uldTypeData.formControl.setValue($event as string);
        break;
      case ColumnIds.ORIGIN_SORT:
        this.originSortData.formControl.setValue($event as string);
        break;
      default:
        break;
    }
    this.search();
  }

  /**
   * Filtering through the search bar
   *
   * @memberof ActiveCarryOverLoadsTableComponent
   */
  search() {
    this.commonUtils.searchFilter(
      [
        this.uldNumberData,
        this.sealNumberData,
        this.loadKeyData,
        this.loadChartData,
        this.movementData,
        this.pkgCountData,
        this.loadCreateData,
      ],
      this.dataSource,
      [this.uldTypeData, this.originSortData],
    );
  }

  /**
   * Clear all the filters if user has searched/filtered any column
   *
   * @memberof InterceptSummaryComponent
   */
  clearAllFilters() {
    this.commonUtils.clearAllFilters(
      this.dataSource,
      this.originalData,
      [
        this.uldNumberData.formControl,
        this.sealNumberData.formControl,
        this.loadKeyData.formControl,
        this.loadChartData.formControl,
        this.movementData.formControl,
        this.pkgCountData.formControl,
        this.loadCreateData.formControl,
      ],
      [this.uldTypeData, this.originSortData],
    );
    this.resetSelectDropdowns.next(true);
  }

  openUldCountPopup(activeCarryOverLoads: ActiveCarryOverLoads, index: number) {
    if (this.selectedUldNumberIndex !== index) {
      this.selectedUldNumberIndex = null;
    }
    const dialogRef = this._dialog.open(UldCountModalComponent, {
      width: '88.75rem',
      disableClose: true,
      data: {
        ...activeCarryOverLoads,
        selectedUldNumberIndex: this.selectedUldNumberIndex,
        smartBag:
          activeCarryOverLoads.loadKeyresponse?.discriminator === 'DOMESTIC' &&
          this.locationAutoLink,
      },
    });
    dialogRef?.afterClosed().subscribe((resp) => {
      if (resp) {
        this.selectedUldNumberIndex = null;
        this.getActiveLoads();
      }
    });
  }

  submit() {
    if (
      this.carryLoadsForm.get('bay')?.value &&
      this.carryLoadsForm.get('belt')?.value &&
      !this.carryLoadsForm.get('equipmentNumber')?.value &&
      !this.carryLoadsForm.get('equipmentType')?.value
    ) {
      this.getGleLoad();
    } else if (
      this.carryLoadsForm.get('equipmentNumber')?.value &&
      this.carryLoadsForm.get('equipmentType')?.value
    ) {
      this.updateGleLoad();
    } else {
      this.closeTheLoad();
    }
  }

  updateGleLoad() {
    this.carryOverLoadUtils
      .updateGleLoad(this.updateGleRequest.response, this.selectedLoad)
      .subscribe((data) => {
        if (data.code === 200)
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
      });
  }

  getGleLoad() {
    this.carryOverLoadUtils
      .getGleLoad(this.carryLoadsForm.getRawValue(), this.selectedLoad)
      .subscribe((data) => {
        if (data.code === 200) {
          this.showEquipmentDetails = true;
          this.carryLoadsForm
            .get('sealNumber')
            ?.setValue(data.response.sealNumber);
          this.carryLoadsForm
            .get('equipmentNumber')
            ?.setValue(data.response.equipmentNumber);
          this.carryLoadsForm
            .get('equipmentType')
            ?.setValue(data.response.equipmentType);
          this.updateGleRequest = data;
        }
      });
  }

  /**
   * Attempts to close the selected load. If the load is domestic and auto-linking is enabled,
   * it performs a SmartBag check. If the SmartBag check fails (i.e., no bag data is returned),
   * it retries the close operation with the SmartBag check disabled.
   *
   * @param smartBagCheck - A boolean flag indicating whether to perform a SmartBag check.
   *                         Defaults to true. If false, the load is closed without SmartBag validation.
   */
  closeTheLoad(smartBagCheck: boolean = true) {
    // Determine SmartBag check based on load type and auto-linking
    const isDomestic =
      this.selectedLoad?.loadKeyresponse?.discriminator === 'DOMESTIC';
    smartBagCheck = isDomestic && this.locationAutoLink;

    const tableData = this.dataSource.data;
    const closeDate = this.carryLoadsForm.get('closeDate')?.value;
    const closeTime = this.carryLoadsForm.get('closeTime')?.value;
    const uldIndex = this.selectedUldNumberIndex ?? 0;
    const loadId = this.selectedLoad?.id;

    const performClose = (checkSmartBag: boolean) =>
      this.carryOverLoadUtils.closeTheLoad(
        tableData,
        closeDate,
        closeTime,
        this.carryLoadsForm,
        uldIndex,
        checkSmartBag,
        loadId,
        this.locationAutoLink
      );

    performClose(smartBagCheck)
      .pipe(
        switchMap((resp) => {
          if (resp.code !== 200) return of(null);

          // Scenario 1: SmartBagCheck is false originally which means it is either an international load or location has autoLink disabled
          if (!smartBagCheck) {
            this.finalizeClose();
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            this.commonUtils.updateLoadingModal(true);
            return this.activeCarryOverLoadsService.getActiveLoads();
          }

          // Scenario 2: SmartBagCheck is true and bag data is present which means it is a domestic load and location has autoLink enabled
          if (resp.response?.[0]?.bag) {
            const clonedForm = this.fb.group({
              closeDate: [
                this.carryLoadsForm.get('closeDate')?.value,
                Validators.required,
              ],
              closeTime: [
                this.carryLoadsForm.get('closeTime')?.value,
                Validators.required,
              ],
              percentFull: [
                this.carryLoadsForm.get('percentFull')?.value,
                [Validators.required, Validators.min(0), Validators.max(100)],
              ],
            });

            this.dialog
              .open(SmartBagCheckComponentComponent, {
                data: {
                  selectedLoad: this.selectedLoad,
                  response: resp.response,
                  carryLoadsForm: clonedForm,
                },
              })
              .afterClosed()
              .subscribe((data: boolean) => {
                if (data) {
                  this.activeCarryOverLoadsService
                    .getActiveLoads()
                    .subscribe((loads) => {
                      this.activeLoadsDataSet(loads);
                    });
                }
              });

            this.finalizeClose();
            return of(null);
          }

          // Scenario 3: SmartBagCheck is true but no bag data, retry with check disabled
          return of(null).pipe(
            tap(() => {
              this.commonUtils.updateLoadingModal(true);
              this.commonUtils.lockUpdateLoadingModal(true);
            }),
            switchMap(() => performClose(false)),
            switchMap((retryResp) => {
              if (retryResp.code === 200) {
                this.finalizeClose();
                this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
                return this.activeCarryOverLoadsService.getActiveLoads();
              }
              return of(null);
            }),
          );
        }),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
          this.commonUtils.lockUpdateLoadingModal(false);
        }),
      )
      .subscribe((resp) => {
        if (resp) {
          this.activeLoadsDataSet(resp);
        }
      });
  }

  openOutboundPopup(activeCarryOverLoad: ActiveCarryOverLoads) {
    const dialogRef = this._dialog.open(OutboundScanGleModalComponent, {
      width: '35.75rem',
      disableClose: true,
      panelClass: 'dialog-offset-viewport',
      data: {
        carryOverLoad: true,
        activeCarryOverLoad,
        sealNumber: activeCarryOverLoad?.sealNumber,
        movement: activeCarryOverLoad?.movement,
      },
    });
    dialogRef?.afterClosed().subscribe(() => {
      this.getActiveLoads();
    });
  }

  onRadioChange(value: ActiveCarryOverLoads): void {
    const closeDate = this.carryLoadsForm.get('closeDate')?.value;

    this.carryLoadsForm.get('sortCode')?.setValue(value.origSort);
    this.carryLoadsForm.get('sortDate')?.setValue(closeDate);
    this.selectedLoad = value;
  }

  loadKeyUpdate() {
    const origSort =
      this.selectedLoad.origSort.length == 2
        ? this.selectedLoad.origSort?.[0]
        : this.selectedLoad.origSort;
    this.dialog
      .open(AddEditUldModalComponent, {
        width: '34.25rem',
        disableClose: true,
        autoFocus: false,
        data: {
          formData: {
            sortDate: this.carryLoadsForm.value.closeDate,
            sort: origSort,
            equipmentNumber: this.selectedLoad.uldNumber,
            equipmentType: this.selectedLoad.uldType,
            loadKey: this.selectedLoad.loadKey,
          },
          currentStep: 1,
          isLoadKeyUpdate: true,
          selectedLoad: this.selectedLoad,
        },
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp?.updateUld) {
          this.getActiveLoads();
          this.selectedUldNumberIndex = null;
        }
      });
  }

  /**
   * used to add new ULD row top of the table
   *
   * @param {*} uldData
   * @memberof OutboundScanTableComponent
   */
  addNewUldRow(uldData: ModalFormValue) {
    const newRow = {
      ...uldData,
      sealNumber: uldData?.sealControl,
      createSortDate: uldData?.sortDate,
      loadName: uldData?.load,
      loadChart: '',
      parentUld: '',
      type: uldData.equipmentType,
    };
    const tableData = this.dataSource.data;
    tableData.unshift(newRow);
    this.dataSource.data = [...tableData];
  }

  /**
   * Figure out if filter button is enabled
   * Should not be enabled if user has not filtered any column
   *
   * @readonly
   * @memberof InterceptSummaryComponent
   */
  get isFilterEnabled() {
    return (
      this.uldNumberData.formControl.value !== '' ||
      this.sealNumberData.formControl.value !== '' ||
      this.loadKeyData.formControl.value !== '' ||
      this.loadChartData.formControl.value !== '' ||
      this.movementData.formControl.value !== '' ||
      this.pkgCountData.formControl.value !== '' ||
      this.loadCreateData.formControl.value !== '' ||
      this.uldTypeData.ids.length > 0 ||
      this.originSortData.ids.length > 0
    );
  }

  private finalizeClose() {
    this.selectedUldNumberIndex = null;
    this.carryLoadsForm.reset({ closeDate: new Date() });
  }
}
